import React, { useEffect, useRef, useState } from 'react';
import '../../styled/pages/dashboard.scss';
import { Icon } from '@ohif/ui';
import {
  CustomTable,
  Header,
  Pagination,
  Loader,
  ConfirmationModal,
  Success,
  Failed,
} from '../../components';
import { capitalizeFirstLetterOfEachWord, customStyles } from '../../Utils/utils';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { getUserApi } from '../../rtk-apis/users/getUser';
import { deleteUserApi } from '../../rtk-apis/users/deleteUser';
import { clearState } from '../../store/slices/users/getUser';
import { clearState as clearDeleteState } from '../../store/slices/users/deleteUser';
import useDebounce from '../../components/useDebounce';
import { getHospitalId, getRole, getToken } from '../../Utils/authUtils';
import SortComponent from '../../components/SortComponent';
import { CLINICS } from '../../constant';

const UserList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const role = getRole();
  const { status, result, msg, loading } = useSelector((state: RootState) => state.getUserResponse);
  const deleteUserResponse = useSelector((state: RootState) => state.deleteUserResponse);

  const [tableData, setTableData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const searchQuery = useDebounce(searchTerm, 1000);
  const [selectedRow, setSelectedRow] = useState(null);
  const [displayMessage, setDisplayMessage] = useState('');
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [columns, setColumns] = useState([
    { key: 'name', title: 'Name' },
    { key: 'role', title: 'Role' },
    { key: 'clinicName', title: 'Clinic Name' },
    { key: 'email', title: 'Email Address' },
    { key: 'contactDetails', title: 'Contact Details' },
  ]);
  const [selectedClinicId, setSelectedClinicId] = useState('');
  const [clinicOptions, setClinicOptions] = useState<{ _id: string; name: string }[]>([]);
  const [clinicSearch, setClinicSearch] = useState('');
  const [isClinicDropdownOpen, setIsClinicDropdownOpen] = useState(false);
  const clinicDropdownRef = useRef<HTMLDivElement>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 30,
    totalRecords: 0,
    totalPages: 1,
  });

  // Use the data directly from the API response without client-side pagination
  const currentItems = filteredData;

  const hospitalid = role !== 'SUPER_ADMIN' ? getHospitalId() : '';
  const userRole = localStorage.getItem('email') || sessionStorage.getItem('email');

  const actions = [
    {
      label: 'Edit',
      iconName: 'edit-icon',
      onClick: (item: any) => {
        const userIdOrClinicId = item?.role === 'Admin' ? item?.clinicId : item?._id;
        navigate(`/updateuser/${userIdOrClinicId}`, {
          state: { userDetails: item },
        });
      },
    },
    {
      label: 'Delete',
      iconName: 'delete-icon',
      onClick: (item: any) => {
        setSelectedRow(item);
        setIsConfirmationModalOpen(true);
      },
    },
  ];

  // ************************ Use-Effects ****************************************

  useEffect(() => {
    if (role !== 'SUPER_ADMIN') return;
    const fetchClinics = async () => {
      try {
        const res = await fetch(`${CLINICS.GET_CLINIC_URL}?page=1&limit=1000&search=`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data?.data) ? data.data : [];
        const mapped = list.map((c: any) => ({ _id: c._id, name: c.name }));
        mapped.sort((a, b) => a.name.localeCompare(b.name));
        setClinicOptions(mapped);
      } catch {}
    };
    fetchClinics();
  }, [role]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (clinicDropdownRef.current && !clinicDropdownRef.current.contains(e.target as Node)) {
        setIsClinicDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const payload = {
      page: pagination.currentPage,
      limit: pagination.pageSize,
      search: searchQuery,
      hospitalId: hospitalid,
      clinicId: selectedClinicId,
      loggedinEmail: userRole,
    };
    dispatch(getUserApi(payload));
  }, [searchQuery, pagination.currentPage, deleteUserResponse?.status, pagination.pageSize, selectedClinicId]);

  useEffect(() => {
    try {
      if (role === 'Admin') {
        setColumns([
          { key: 'name', title: 'Name' },
          { key: 'role', title: 'Role' },
          { key: 'email', title: 'Email Address' },
          { key: 'contactDetails', title: 'Contact Details' },
        ]);
      }
      if (status === 'SUCCESS') {
        const dataToMap = Array.isArray(result?.data) ? result?.data : [];
        const modifiedResponse = dataToMap.map(item => {
          const clinicName = capitalizeFirstLetterOfEachWord(item?.clinicName);
          const contactDetails =
            item?.role === 'Admin'
              ? item?.countryCode + ' ' + item?.contactNo
              : item?.countryCode + ' ' + item?.contactDetails;
          return {
            ...item,
            contactDetails,
            clinicName,
          };
        });
        setTableData(modifiedResponse);
        setFilteredData(modifiedResponse);

        // Update pagination from API response
        if (result?.pagination) {
          setPagination({
            currentPage: result.pagination.currentPage,
            pageSize: result.pagination.pageSize,
            totalRecords: result.pagination.totalRecords,
            totalPages: result.pagination.totalPages,
          });
        }
      }
      if (status === 'FAILED') {
        setTableData([]);
        setFilteredData([]);
      }
    } catch (error) {
      console.error('Error processing user data:', error);
      setTableData([]);
      setFilteredData([]);
    }
  }, [status]);

  useEffect(() => {
    if (deleteUserResponse?.status === 'SUCCESS') {
      setDisplayMessage(deleteUserResponse?.msg);
      setIsSuccessModalOpen(true);
    }
    if (deleteUserResponse?.status === 'ERROR') {
      setDisplayMessage(deleteUserResponse?.msg);
      setIsFailedModalOpen(true);
    }
    dispatch(clearDeleteState());
  }, [deleteUserResponse.status]);

  useEffect(() => {
    const filtered = tableData?.filter(item =>
      item?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(filtered);
  }, [searchTerm, tableData]);

  const handleDeleteClinic = () => {
    const payload = {
      _id: selectedRow?._id,
      _rev: selectedRow?._rev,
      ...(selectedRow?.type === 'CLINIC_ADMIN' && { email: selectedRow?.email }),
    };
    dispatch(deleteUserApi(payload));
    setSelectedRow(null);
  };

  return (
    <section
      className="layoutContainer"
      style={customStyles.layoutBackground}
    >
      <div className="layoutGrid">
        <div className="page page--fullBody">
          <Header />
          <div className="page__body">
            <div className="page__content">
              <div className="page__body__head">
                <h1 className="title title--h6 title--normal">Manage Users</h1>
                <div className="btn__group">
                  <button
                    type="button"
                    className="btn btn__primary"
                    onClick={() => {
                      navigate('/adduser');
                    }}
                  >
                    <Icon name="add-icon" />
                    Add User
                  </button>
                </div>
              </div>
              <div className="tableWrap">
                <div className="tableWrap__head">
                  {/* Search */}
                  <div className="formRow">
                    <div className="formCol relative">
                      <input
                        className="form__input form__input--search"
                        type="text"
                        placeholder="Search by Name"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e?.target?.value)}
                      />
                      {searchTerm && (
                        <span
                          className="absolute right-8 bottom-2 cursor-pointer"
                          onClick={() => setSearchTerm('')}
                        >
                          <Icon name="clear-icon" />
                        </span>
                      )}
                      <span className="absolute right-4 bottom-3 cursor-pointer">
                        <Icon name="search-icon" />
                      </span>
                    </div>
                    {role === 'SUPER_ADMIN' && (
                      <div className="formCol" ref={clinicDropdownRef} style={{ position: 'relative' }}>
                        <input
                          type="text"
                          className="form__input"
                          placeholder={selectedClinicId ? (clinicOptions.find(c => c._id === selectedClinicId)?.name || 'Search clinic...') : 'Search clinic...'}
                          value={clinicSearch}
                          onChange={e => {
                            setClinicSearch(e.target.value);
                            setIsClinicDropdownOpen(true);
                          }}
                          onFocus={() => setIsClinicDropdownOpen(true)}
                        />
                        {isClinicDropdownOpen && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 100,
                            background: '#0c2137',
                            border: '1px solid #1e3a5f',
                            borderRadius: '4px',
                            maxHeight: '240px',
                            overflowY: 'auto',
                          }}>
                            <div
                              style={{ padding: '8px 12px', cursor: 'pointer', color: '#ccc', borderBottom: '1px solid #1e3a5f' }}
                              onMouseDown={() => {
                                setSelectedClinicId('');
                                setClinicSearch('');
                                setIsClinicDropdownOpen(false);
                                setPagination(prev => ({ ...prev, currentPage: 1 }));
                              }}
                            >
                              All Clinics
                            </div>
                            {clinicOptions
                              .filter(c => {
                                const hay = c.name.toLowerCase();
                                const needle = clinicSearch.toLowerCase();
                                let hi = 0;
                                for (let i = 0; i < hay.length && hi < needle.length; i++) {
                                  if (hay[i] === needle[hi]) hi++;
                                }
                                return hi === needle.length;
                              })
                              .map(c => (
                                <div
                                  key={c._id}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    color: c._id === selectedClinicId ? '#fff' : '#ccc',
                                    background: c._id === selectedClinicId ? '#1e3a5f' : 'transparent',
                                  }}
                                  onMouseDown={() => {
                                    setSelectedClinicId(c._id);
                                    setClinicSearch('');
                                    setIsClinicDropdownOpen(false);
                                    setPagination(prev => ({ ...prev, currentPage: 1 }));
                                  }}
                                >
                                  {c.name}
                                </div>
                              ))
                            }
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="btn__group">
                    {/* <Filter /> */}
                    <SortComponent
                      searchQuery={searchQuery}
                      sortKey="name"
                      data={tableData}
                      setFilteredData={setFilteredData}
                    />
                  </div>
                </div>
                <CustomTable
                  actions={actions}
                  columns={columns}
                  tableData={currentItems}
                />
              </div>
            </div>
          </div>
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            itemsPerPage={pagination.pageSize}
            totalItems={pagination.totalRecords}
            onPageChange={page => {
              setPagination(prev => ({
                ...prev,
                currentPage: page,
              }));
            }}
            onItemsPerPageChange={size => {
              setPagination(prev => ({
                ...prev,
                pageSize: size,
                currentPage: 1, // Reset to first page when changing page size
              }));
            }}
          />
        </div>
      </div>
      <Loader isLoading={loading || deleteUserResponse.loading} />
      <ConfirmationModal
        title="Delete User"
        description="Are you sure you want to delete this user?"
        isConfirmationModalOpen={isConfirmationModalOpen}
        handleCloseModal={() => setIsConfirmationModalOpen(false)}
        handleConfirm={() => {
          setIsConfirmationModalOpen(false);
          handleDeleteClinic();
        }}
        cancelActionText="Cancel"
        confirmActionText="Yes"
        isPatientReport={true}
      />
      <Success
        title={displayMessage}
        isSuccessModalOpen={isSuccessModalOpen}
        handleCloseModal={() => setIsSuccessModalOpen(false)}
      />
      <Failed
        title={displayMessage}
        isFailedModalOpen={isFailedModalOpen}
        handleCloseModal={() => setIsFailedModalOpen(false)}
      />
    </section>
  );
};

export default UserList;
